/**
 * MobileFilterDrawer — Slide-in drawer para mostrar GlobalFilterBar en móvil.
 * Solo visible en pantallas pequeñas (md:hidden).
 */
import React, { useEffect } from 'react';
import { X, SlidersHorizontal } from 'lucide-react';
import { GlobalFilterBar } from '@/components/dashboard/GlobalFilterBar';
import { useFiltersStore, getActiveFilterCount } from '@/store/filtersStore';

export function MobileFilterDrawer({ open, onClose }) {
  const activeCount = useFiltersStore(getActiveFilterCount);
  const clearFilters = useFiltersStore(s => s.clearFilters);

  // Lock scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        style={{ background: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl overflow-y-auto"
        style={{
          background: 'var(--vz-bg-card)',
          borderTop: '1px solid var(--vz-border-subtle)',
          maxHeight: '80vh',
          paddingBottom: '4rem',
        }}
        data-testid="mobile-filter-drawer"
      >
        {/* Handle */}
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--vz-border-subtle)' }}>
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={14} style={{ color: 'var(--vz-text-muted)' }} />
            <span className="text-sm font-semibold font-montserrat" style={{ color: 'var(--vz-text-primary)' }}>
              Filtros
            </span>
            {activeCount > 0 && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                style={{ background: 'rgba(74,158,255,0.15)', color: 'var(--vz-blue)' }}
              >
                {activeCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {activeCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-[10px] font-medium"
                style={{ color: 'var(--vz-text-muted)' }}
                data-testid="mobile-filter-clear"
              >
                Limpiar todo
              </button>
            )}
            <button onClick={onClose} data-testid="mobile-filter-close">
              <X size={18} style={{ color: 'var(--vz-text-muted)' }} />
            </button>
          </div>
        </div>

        {/* Filter content — use GlobalFilterBar in "embedded" mode */}
        <div className="px-4 py-3">
          <GlobalFilterBar embedded />
        </div>

        {/* Apply button */}
        <div className="px-4 pb-4">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl font-semibold text-sm font-montserrat"
            style={{ background: 'var(--vz-green)', color: '#fff' }}
            data-testid="mobile-filter-apply"
          >
            Aplicar filtros{activeCount > 0 ? ` (${activeCount})` : ''}
          </button>
        </div>
      </div>
    </>
  );
}
